/* eslint react/no-danger: 0 */

import { h, Fragment } from 'preact';
import { useEffect, useMemo, useState, useRef } from 'preact/hooks';
import clsx from 'clsx';
import Image from '../utils/image';
import { useWindowSize } from '../utils/window-size';
import useIsInViewport from '../utils/isInViewPort';
import '../utils/countdown.min';
import SpecificationLink from './product-specifications';
import AkoaLink from './product-akoa';
import { decodeId } from '../utils/storefront/get-products';
import BuildIcon from '../utils/icons/buildicon.svg';
import CalendarIcon from '../utils/icons/calendar.svg';
// commented out until we build lifesharp
// import ExpandIcon from '../utils/icons/expand.svg';
import Price from './product-price';
import getLineItemAttributes from '../utils/storefront/get-attributes';
import { addToCartTrigger, viewItemTrigger } from '../utils/analytics';
import {
  formatMoney,
  getProductBadge,
  checkInventoryTreshold,
  makeDivHorizontallyScrollable,
  elevarTracking,
} from '../utils/helpers';
import { CartClearWarningModal } from '../utils/cart-clear-warning-modal';
import { postData } from '../utils/klaviyo';
import ThreekitIcons from '../product-customizer/components/threekit-icons';
import { formatMetaobject } from '../utils/storefront/format-resource';
import useAutoOpenCustomizer from '../utils/hooks/useAutoOpenCustomizer';

const AtcButton = ({ onClick, label, type = 'button', disabled }) => (
  <button
    type={type}
    className="btn-primary-fill bg-signature rounded-sm text-center border-signature uppercase w-full font-bold mb-2.5"
    onClick={onClick}
    disabled={disabled}
  >
    {label}
  </button>
);

const subscribeToNewsletter = (email) => {
  const listId = window.Benchmade.klaviyoEmailListId;
  if (listId === '') {
    /* eslint-disable-next-line no-console */
    console.error('Klaviyo Email List ID is not set');
    return;
  }

  const formData = {
    profiles: [{ email }],
  };

  postData(
    `/apps/service/klaviyo/api/v2/list/${listId}/subscribe`,
    formData,
  ).then(({ status }) => {
    if (!status) {
      /* eslint-disable-next-line no-console */
      console.error('There was an error subscribing to the Newsletter');
    }
  });
};

const Title = ({ currentProduct, isGift }) => (
  <div
    className={`${
      !isGift
        ? 'border pb-5 border-grey-400 border-t-0 border-l-0 border-r-0 laptop:pb-[23px]'
        : 'pb-1 laptop:pb-5'
    } col-span-12`}
  >
    <h1 className="heading-3">{currentProduct.title}</h1>
  </div>
);

function updateHighlightedReviewCount(count) {
  const reviewStars = document.querySelector(
    '.yotpo-shoppers-say .single-best-sentence .review-stars',
  );
  if (!reviewStars || count < 1) return;

  reviewStars.setAttribute('data-reviews-count', count);
  // Force re-apply styles dynamically
  const styleSheet = document.createElement('style');
  styleSheet.innerHTML = `
    .yotpo-shoppers-say .single-best-sentence .review-stars::after {
      content: ' / ${count} REVIEWS';
      display: inline-block;
      color: #000;
      position: absolute;
      left: 10px;
    }
    @media (min-width: 640px) {
      .yotpo-shoppers-say .single-best-sentence .review-stars::after {
        content: '(${count})';
        display: inline-block;
        color: #000;
        position: unset;
        font-size: 18px;
        line-height: 22px;
        margin-left: 15px;
      }
    }
  `;
  document.head.appendChild(styleSheet);
}

const Stock = ({ currentVariant, currentProduct }) => {
  const hasComingSoonDate = currentProduct?.comingSoonDate?.value;
  const isSoldOut = !hasComingSoonDate && currentVariant.quantityAvailable < 1;
  const isLowInventory = checkInventoryTreshold(currentVariant);
  const productBadge = getProductBadge(currentProduct.tags, 'badge_pdp:');
  const productBadgeDiscount = getProductBadge(
    currentProduct.tags,
    'pdp_badge_discount:',
  );
  const hasCompareAtPrice = currentVariant?.compareAtPrice?.amount;

  return isSoldOut || isLowInventory || productBadgeDiscount || productBadge ? (
    <div
      className={clsx('col-span-12 justify-center flex pt-1 laptop:pr-5', {
        'laptop:col-span-12 laptop:py-0 laptop:justify-start laptop:pb-[17px]':
          hasCompareAtPrice && !productBadgeDiscount,
        'laptop:col-span-6 laptop:py-[17px] laptop:justify-end':
          !hasCompareAtPrice || productBadgeDiscount,
      })}
    >
      <span className="font-m-16 text-accent-1 tracking-[-1px]">
        {isSoldOut && <>{window.TMW.Strings.product.soldOut}</>}
        {isLowInventory && !hasComingSoonDate && (
          <>{`Only ${currentVariant.quantityAvailable} Left!`}</>
        )}
        {!isLowInventory &&
          !isSoldOut &&
          ((productBadgeDiscount && (
            <>
              {window.TMW.Strings.product.badgeDiscountBeforeText}
              {productBadgeDiscount}
              {window.TMW.Strings.product.badgeDiscountAfterText}
            </>
          )) ||
            (productBadge && <>{productBadge}</>))}
      </span>
    </div>
  ) : null;
};

const CountDown = ({ currentProduct }) => {
  const [days, setDays] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');

  const { countdown } = window;
  const comingSoonDate = currentProduct?.comingSoonDate?.value;

  const [comingSoonCountdown, setComingSoonCountdown] = useState(
    countdown(
      null,
      new Date(comingSoonDate),
      // eslint-disable-next-line
      countdown.DAYS | countdown.MINUTES | countdown.HOURS,
    ),
  );

  useEffect(() => {
    if (!comingSoonDate || !countdown) return null;

    function updateCountdown() {
      setComingSoonCountdown(
        countdown(
          null,
          new Date(comingSoonDate),
          // eslint-disable-next-line
          countdown.DAYS | countdown.MINUTES | countdown.HOURS,
        ),
      );
    }

    updateCountdown();
    setInterval(updateCountdown, 60000);
    return () => clearInterval(updateCountdown);
  }, [comingSoonDate, countdown]);

  useEffect(() => {
    setDays(comingSoonCountdown.days);
    setHours(comingSoonCountdown.hours);
    setMinutes(comingSoonCountdown.minutes);
  }, [comingSoonCountdown]);

  return (
    <div className="col-span-6 font-[325] font-gotham text-xxs text-accent-1 leading-[14px] border border-grey-400 border-t-0 border-b-0 border-r-0 text-center pt-3.5 pb-6 tablet:pt-5 tablet:pb-3 laptop:py-0 laptop:pl-4 laptop:flex items-center justify-between h-full">
      <p className="text-xs leading-4 mb-0.5 laptop:mb-0">Coming soon!</p>
      {!comingSoonDate || new Date() > new Date(comingSoonDate) ? null : (
        <p>
          {days}Days. {hours}Hrs. {minutes}Mins
        </p>
      )}
    </div>
  );
};

const Afterpay = ({ currentVariant, currentProduct }) => {
  if (
    (currentVariant.afterpayMessage &&
      currentVariant.afterpayMessage.value === 'false') ||
    currentVariant.type === 'Gift Card' ||
    currentProduct.tags.some(
      (tag) =>
        tag.includes('cstm:') &&
        !(tag.includes('cstm:service') || tag.includes('cstm:personalization')),
    )
  ) {
    return null;
  }
  return (
    <>
      <afterpay-placement
        data-amount={currentVariant.priceV2.amount}
        data-locale="en_US"
        data-currency="USD"
        data-logo-type="lockup"
        data-size="xs"
        className="laptop:text-right"
      />
      <span className="hidden laptop:flex laptop:justify-center mx-1.5 font-r-12 text-primary-1 laptop:min-w-[24px]">
        /
      </span>
    </>
  );
};

const Benefits = ({ benefitsData, setIsOpenExplore, madeIn }) => {
  const benefitsCount = madeIn
    ? benefitsData.benefits_blocks.length + 1
    : benefitsData.benefits_blocks.length;
  return (
    <div className="grid grid-cols-12 col-span-12 border-2 border-b-0 border-l-0 border-r-0 border-primary-1">
      {benefitsData.title && (
        <div className="col-span-12 pt-4 pb-5 laptop:py-3.5 border border-grey-400 border-t-0 border-l-0 border-r-0 text-center">
          <div className="flex items-center justify-between">
            <h6 className="heading-6 text-primary-1 upppercase">
              {benefitsData.title}
            </h6>
            <button
              type="button"
              aria-label="Explore More"
              onClick={() => setIsOpenExplore(true)}
              className="underline cursor-pointer font-r-12 text-primary-1"
            >
              Explore More
            </button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-12 col-span-12 border-b border-grey-400">
        {madeIn && (
          <div
            className={`col-span-12 laptop:col-span-${
              12 / benefitsCount
            } border border-grey-400 border-t-0 border-b laptop:border-b-0 border-l-0 border-r-0 laptop:border-r flex flex-row-reverse laptop:flex-col justify-between laptop:justify-start items-center py-1 laptop:pt-3 laptop:pb-4 px-2`}
          >
            <div className="flex items-center justify-center w-9 h-[42px]">
              <Image
                src={madeIn.image.url}
                width={80}
                height={80}
                alt={`Benefit image for ${madeIn.heading}`}
                className="w-9"
              />
            </div>
            <p className="text-center font-m-12 text-primary-1">
              {madeIn.heading}
            </p>
          </div>
        )}
        {benefitsData.benefits_blocks.map((benefit, index) => {
          const classes =
            index === benefitsData.benefits_blocks.length - 1
              ? `col-span-12 laptop:col-span-${
                  12 / benefitsCount
                } flex flex-row-reverse laptop:flex-col justify-between laptop:justify-start items-center py-1 laptop:pt-3 laptop:pb-4 px-2`
              : `col-span-12 laptop:col-span-${
                  12 / benefitsCount
                } border border-grey-400 border-t-0 border-b laptop:border-b-0 border-l-0 border-r-0 laptop:border-r flex flex-row-reverse laptop:flex-col justify-between laptop:justify-start items-center py-1 laptop:pt-3 laptop:pb-4 px-2`;
          return (
            <div className={classes} key={index}>
              <div className="flex items-center justify-center w-9 h-[42px]">
                <Image
                  src={benefit.image}
                  width={80}
                  height={80}
                  alt={`Benefit image for ${benefit.text}`}
                  className="w-9"
                />
              </div>
              <p className="text-center font-m-12 text-primary-1">
                {benefit.heading}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// commented out until we build lifesharp
// const MembershipItem = ({
//   membership,
//   currentMembership,
//   setCurrentMembership,
// }) => (
//   <div
//     onClick={() => setCurrentMembership(membership)}
//     className={`col-span-12 laptop:col-span-6 ${
//       membership.title === currentMembership.title
//         ? 'opacity-100'
//         : 'opacity-50'
//     } p-3 border border-[#222] rounded-sm cursor-pointer laptop:h-[100px]`}
//   >
//     <div className="flex items-center justify-start mb-2">
//       <h4 className="mr-2 font-m-14">{membership.title}</h4>
//       <p className="font-r-12 text-accent-1">{membership.price}</p>
//     </div>
//     <div>
//       <p className="font-r-10">{membership.description}</p>
//     </div>
//   </div>
// );

const ShippingMessage = ({ currentProduct, shippingMessages }) => {
  const is3D =
    currentProduct.tags.filter((tag) => tag.includes('cstm:')).length > 0 &&
    !(
      currentProduct.tags.includes('cstm:personalization') ||
      currentProduct.tags.includes('cstm:service')
    );
  return (
    <span className="font-r-12 text-primary-1">
      {is3D
        ? shippingMessages?.shipping_text_3d
        : shippingMessages?.shipping_text_non_3d}
    </span>
  );
};

const Reviews = ({ yotpoReviewsUpdated }) => {
  const [reviewsHTML, setReviewsHTML] = useState('');

  useEffect(() => {
    const intervalId = setInterval(() => {
      const reviewsContainer = document.querySelector('#star-rating');
      if (
        reviewsContainer &&
        reviewsContainer.querySelector('.yotpo-sr-bottom-line-summary')
      ) {
        updateHighlightedReviewCount(
          parseInt(
            reviewsContainer.querySelector('.yotpo-sr-bottom-line-text')
              ?.innerHTML || '',
            10,
          ),
        );
        setReviewsHTML(reviewsContainer.innerHTML);
        reviewsContainer.style.display = 'none';
        clearInterval(intervalId); // Clear the interval once the conditions are met
      }
    }, 500); // Check every 500ms

    return () => clearInterval(intervalId); // Clear the interval when the component unmounts
  }, [yotpoReviewsUpdated]);

  return (
    <a
      href="#product-reviews"
      className="flex items-center justify-center col-span-6 pt-5 pb-3 text-center underline border border-t-0 border-b-0 border-r-0 font-r-12 border-grey-400 laptop:py-0 laptop:h-full"
    >
      <div dangerouslySetInnerHTML={{ __html: reviewsHTML }} />
    </a>
  );
};

const BuildButton = ({
  currentVariant,
  currentProduct,
  customProduct,
  isDisabled,
}) => {
  const customTag = currentProduct?.tags?.find((tag) => tag.includes('cstm'));
  const handleClick = async (e) => {
    e.preventDefault();
    const customTag = (customProduct?.tags || []).find((tag) =>
      tag.includes('cstm:'),
    );
    const threekitAssetId = customProduct?.threekitAssetId?.value || null;
    const threekitDefaultId = customProduct?.threekitDefaultId?.value || null;

    window.initializeCustomizerPlayer({
      assetId: threekitAssetId,
      customTag,
      handle: customProduct?.handle,
      defaultConfigId: threekitDefaultId,
    });
  };

  return customTag ? (
    <>
      <button
        onClick={(e) => handleClick(e)}
        className="w-full py-2 font-bold text-center uppercase rounded-sm btn btn-primary-stroke"
        disabled={isDisabled || !currentVariant.available}
      >
        {customTag === 'cstm:personalization' ? (
          <span className="inline-block py-2">Personalize</span>
        ) : (
          <>
            <BuildIcon
              viewBox={`0 0 49 36`}
              className={`inline-block align-middle !h-auto w-[33px] mr-2`}
            />
            <span className="inline-block">Customize</span>
          </>
        )}
      </button>
    </>
  ) : null;
};

const AddToCartButton = ({
  currentVariant,
  currentProduct,
  showPrice,
  isGift,
  isSimple,
  isComingSoon,
  isBackInStock,
  sectionData,
  isPreOrder,
  data,
  gwpThreshold,
  recipientInfo,
}) => {
  const [warningTitle, setWarningTitle] = useState(null);
  const [warningText, setWarningText] = useState(
    sectionData?.settings?.service_form_warning_modal_text,
  );
  const warningType = useRef(null);
  const variantPrice = formatMoney(
    { amount: currentVariant.priceV2.amount * 100 },
    true,
  );
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isRestrictionOpen, setIsRestrictionOpen] = useState(false);
  const customTag = currentProduct?.tags?.find((tag) => tag.includes('cstm'));
  const is3DKnife = customTag && !customTag.includes('personalization');

  const akoaRequired = currentProduct?.tags?.includes('automatic_knife');
  const akoaAccepted = sessionStorage.getItem('accepted_akoa');

  const isAvailable = currentVariant.available;

  const addToCart = async () => {
    const lineAttributes = getLineItemAttributes({
      data: currentProduct,
      variant: currentVariant,
      isLineItem: false,
      gwpThreshold,
    });

    let lineProperties = [];
    if (currentVariant.compareAtPrice) {
      lineProperties = [
        {
          key: '_compare_price',
          value: formatMoney(
            { amount: currentVariant.compareAtPrice.amount * 100 },
            true,
          ),
        },
      ];
    }

    lineProperties = [
      ...lineProperties,
      {
        key: '_is_simple',
        value: 'true',
      },
    ];

    Object.keys(recipientInfo).forEach((k) => {
      lineProperties.push({ key: k, value: recipientInfo[k] });
    });

    if (Object.keys(recipientInfo).length) {
      lineProperties.push({
        key: '__shopify_send_gift_card_to_recipient',
        value: 'true',
      });
    }

    const products = [
      {
        id: currentVariant.sku,
        name: currentVariant.product.title,
        brand: currentProduct.vendor,
        category: currentProduct.productType,
        variant: currentVariant.title,
        price: `${currentVariant.priceV2.amount}`,
        quantity: '1',
        position: '1',
        list: document.referrer,
        product_id: currentProduct.id.split('/Product/')[1],
        variant_id: currentVariant.id.split('/ProductVariant/')[1],
        compare_at_price: `${data?.compare_at_price}` || '0.00',
        image: currentVariant.image.src || '',
        url: window.location.href,
      },
    ];

    elevarTracking({
      customerId: data?.customerId,
      products,
      event: 'dl_add_to_cart',
    });

    await window.GlobalCartContext.refresh();

    const cart = window.GlobalCartContext;

    window._learnq = window._learnq || [];
    const cartData = {
      total_price: cart?.state.subtotal?.amount ?? 0,
      $value: cart?.state.subtotal?.amount ?? 0,
      original_total_price: cart?.state.subtotal?.amount ?? 0,
      items: cart?.state?.lines,
    };
    window._learnq.push(['track', 'Added to Cart', cartData]);

    const isOtherPreOrderInCart = (cart?.state?.lines || []).find(
      (line) =>
        line.merchandise.product?.tags?.includes('pre-order') &&
        line.merchandise.product.id !== currentProduct.id,
    );

    if (isOtherPreOrderInCart) {
      setIsRestrictionOpen(true);
      return;
    }

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          id: currentVariant.id,
          quantity: 1,
          attributes: [...lineAttributes, ...lineProperties],
        },
        trackingPayload: {
          item: currentProduct.id?.split('/Product/')[1],
          q: 1,
          v: currentVariant.id?.split('/ProductVariant/')[1],
        },
      },
    });
    addToCartTrigger(currentProduct, currentVariant);

    document.dispatchEvent(addToCartEvent);
  };

  const handleAddToCart = (e) => {
    e.preventDefault();

    const hasWarranty = window.GlobalCartContext?.state?.lines?.some((line) =>
      line.merchandise?.product?.handle?.startsWith('service-request-product'),
    );

    if (isGift) {
      const form = document.querySelector('[data-product-form]');
      const formData = new FormData(form);
      for (const [key] of formData.entries()) {
        if (key === 'Recipient email') {
          const emailContainerId = '#recipient_email_container';
          const emailContainer = document.querySelector(emailContainerId);
          const emailInput = emailContainer.querySelector('#recipient_email');
          const emailErrorId = '#recipient_email_error';
          const emailErrorText = emailContainer.querySelector(emailErrorId);
          if (!emailInput.value || !emailInput.checkValidity()) {
            emailErrorText.classList.remove('hidden');
            emailErrorText.setAttribute('role', 'alert');
            return;
          }

          emailErrorText.classList.add('hidden');
        }

        if (key === 'Send on') {
          const dateInput = document.getElementById('send_date');

          const sendOnDateError = '#send_on_error';
          const sendOnDateErrorText = document.querySelector(sendOnDateError);

          if (!dateInput.value || !dateInput.reportValidity()) {
            sendOnDateErrorText.classList.remove('hidden');
            sendOnDateErrorText.setAttribute('role', 'alert');
            return;
          }

          sendOnDateErrorText.classList.add('hidden');
        }
      }
    }

    if (
      (isPreOrder &&
        window.GlobalCartContext?.state?.lines.some(
          (item) => !item.merchandise?.product?.tags?.includes('pre-order'),
        )) ||
      (!isPreOrder &&
        window.GlobalCartContext?.state?.lines.some((item) =>
          item.merchandise?.product?.tags?.includes('pre-order'),
        ))
    ) {
      setWarningTitle(window.TMW.Strings.product.preOrderWarning);
      if (isPreOrder) {
        setWarningText(window.TMW.Strings.product.preOrderWarningText);
      } else {
        setWarningText(window.TMW.Strings.product.nonPreOrderWarningText);
      }
      setIsWarningOpen(true);
      warningType.current = isPreOrder ? 'preOrder' : 'nonPreOrder';
      return;
    }
    if (hasWarranty) {
      setWarningTitle(null);
      setWarningText(sectionData?.settings?.service_form_warning_modal_text);
      setIsWarningOpen(true);
      warningType.current = 'warranty';
      return;
    }
    addToCart();
  };

  const onContinue = () => {
    const cart = window.GlobalCartContext;
    if (cart?.state?.lines?.length) {
      cart
        .remove(
          cart.state.lines
            .filter((line) => {
              if (warningType.current === 'preOrder') {
                return !line.merchandise.product?.tags?.includes('pre-order');
              }
              if (warningType.current === 'nonPreOrder') {
                return line.merchandise.product?.tags?.includes('pre-order');
              }
              return true;
            })
            .map((line) => line.id),
          false,
        )
        .then(() => {
          setIsWarningOpen(false);
          addToCart();
        });
    } else addToCart();
  };

  const onRestrictionContinue = () => {
    const cart = window.GlobalCartContext;
    if (cart?.state?.lines?.length) {
      cart
        .remove(
          cart.state.lines
            .filter(
              (line) =>
                line.merchandise.product?.tags?.includes('pre-order') &&
                line.merchandise.product.id !== currentProduct.id,
            )
            .map((line) => line.id),
          false,
        )
        .then(() => {
          setIsRestrictionOpen(false);
          addToCart();
        });
    } else addToCart();
  };

  const onCancel = () => {
    if (warningType.current === 'preOrder') {
      setIsWarningOpen(false);
      return;
    }
    if (warningType.current === 'nonPreOrder') {
      setIsWarningOpen(false);
      return;
    }
    window.location = window.GlobalCartContext.state.checkoutUrl;
  };

  const buttonLabel = useMemo(() => {
    const { product } = window.TMW.Strings;
    const addToCart = isPreOrder ? product.preOrder : product.addToCart;

    if (!currentVariant) {
      return product.unavailable;
    }

    if (!isAvailable) {
      return product.outOfStock;
    }

    if (showPrice) {
      return `${addToCart} - ${variantPrice}`;
    }

    return addToCart;
  }, [isPreOrder, currentVariant, isAvailable, showPrice, variantPrice]);

  return (
    !isBackInStock &&
    (!isComingSoon || isPreOrder) && (
      <>
        <CartClearWarningModal
          onContinue={onRestrictionContinue}
          onCancel={() => setIsRestrictionOpen(false)}
          isOpen={isRestrictionOpen}
          setIsOpen={setIsRestrictionOpen}
          warningTitle={window.TMW.Strings.product.preOrderRestriction}
          warningText={window.TMW.Strings.product.preOrderRestrictionText}
        />
        <CartClearWarningModal
          onContinue={onContinue}
          onCancel={onCancel}
          isOpen={isWarningOpen}
          setIsOpen={setIsWarningOpen}
          warningTitle={warningTitle}
          warningText={warningText}
        />
        <div className="col-span-12 mb-3 laptop:mb-2 atc-buttons">
          {!is3DKnife && (
            <AtcButton
              onClick={handleAddToCart}
              label={buttonLabel}
              type="submit"
              disabled={akoaRequired && !akoaAccepted}
            />
          )}
          {!isGift && !isSimple && !isPreOrder && (
            <BuildButton
              customProduct={currentProduct}
              currentVariant={currentVariant}
              currentProduct={currentProduct}
              isDisabled={!isAvailable || (akoaRequired && !akoaAccepted)}
            />
          )}
        </div>
      </>
    )
  );
};

const OosFormInner = ({
  sectionData,
  handleSubmit,
  isVisibleForm,
  isFormSubmitted,
  isFormInvalid,
  email,
  handleInputChange,
  isSubscribed,
  setIsSubscribed,
  setIsVisibleForm,
  isComingSoon,
}) => {
  const oosType = isComingSoon ? 'coming_soon' : 'bis';
  const data = {
    success_title: sectionData.settings[`${oosType}_success_title`],
    success_message: sectionData.settings[`${oosType}_success_message`],
    error_message: sectionData.settings[`${oosType}_error_message`],
    placeholder_content: sectionData.settings[`${oosType}_placeholder_content`],
    cta_content: sectionData.settings[`${oosType}_cta_content`],
    success_cta_content: sectionData.settings[`${oosType}_success_cta_content`],
    success_cta_link: sectionData.settings[`${oosType}_success_cta_link`],
  };

  return (
    <div className="col-span-12 mb-3 laptop:mb-2">
      {isVisibleForm ? (
        <form onSubmit={handleSubmit}>
          {isFormSubmitted && (
            <div>
              <div className="mb-3 font-m-16 text-primary-1">
                {data.success_title}
              </div>
              <div className="inline-block mb-8 text-xs font-r-12 text-primary-1">
                {data.success_message}
              </div>
            </div>
          )}
          {isFormInvalid && (
            <div className="inline-block mb-3 text-xs text-accent-1">
              {data.error_message}
            </div>
          )}
          {!isFormSubmitted && (
            <input
              type="email"
              value={email}
              required={true}
              onChange={handleInputChange}
              name="email"
              placeholder={data.placeholder_content}
              className="uppercase w-full font-r-10 mb-3 border border-grey-900 px-4 py-[17px] placeholder:font-r-10 placeholder:text-secondary-1"
            />
          )}
          {!isFormSubmitted && (
            <button
              type="submit"
              className="w-full mb-3 font-bold text-center uppercase rounded-sm btn-primary-fill bg-signature border-signature"
            >
              {data.cta_content}
            </button>
          )}
          {isFormSubmitted &&
            data.success_cta_content &&
            data.success_cta_link && (
              <a
                className="w-full mb-3 font-bold text-center uppercase rounded-sm btn-primary-fill bg-signature border-signature"
                href={data.success_cta_link}
              >
                {data.success_cta_content}
              </a>
            )}
          {!isFormSubmitted && (
            <div className="newsletter-checkbox">
              <input
                checked={isSubscribed}
                onChange={() => setIsSubscribed((current) => !current)}
                className="input-checkbox appearance-none inline-block align-middle h-4.5 w-4.5 border border-black checked:border-black checked:hover:border-black checked:focus:border-black rounded-[1px] transition duration-200 mr-2 cursor-pointer"
                type="checkbox"
                id="newsletter-checkbox"
              />
              <label
                className="inline-block text-xs text-black"
                for="newsletter-checkbox"
              >
                I would like to receive the Benchmade Newsletter
              </label>
            </div>
          )}
        </form>
      ) : (
        <AtcButton onClick={setIsVisibleForm} label={data.cta_content} />
      )}
    </div>
  );
};

const ComingSoon = ({
  currentProduct,
  sectionData,
  isVisibleForm,
  setIsVisibleForm,
  isComingSoon,
}) => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(true);
  const [email, setEmail] = useState(
    window.Benchmade.customerEmail ? window.Benchmade?.customerEmail : '',
  );

  const isFormInvalid = false;

  const handleInputChange = (event) => {
    const { value } = event.target;
    setEmail(value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = {
      profiles: [{ email }],
    };

    const comingSoonListId = currentProduct.comingSoonListID?.value;
    if (comingSoonListId === '') {
      /* eslint-disable-next-line no-console */
      console.error('Coming Soon List ID has not been set');
      return;
    }

    postData(
      `/apps/service/klaviyo/api/v2/list/${comingSoonListId}/subscribe`,
      formData,
    ).then(({ status }) => {
      if (status) {
        setIsFormSubmitted(true);
      } else {
        /* eslint-disable-next-line no-console */
        console.error('There was an error submitting the Coming Soon form');
      }
      if (isSubscribed) {
        subscribeToNewsletter(email);
      }
    });
  };

  return (
    <>
      {isComingSoon ? (
        <OosFormInner
          isComingSoon={isComingSoon}
          currentProduct={currentProduct}
          sectionData={sectionData}
          handleSubmit={handleSubmit}
          isVisibleForm={isVisibleForm}
          isFormSubmitted={isFormSubmitted}
          isFormInvalid={isFormInvalid}
          email={email}
          handleInputChange={handleInputChange}
          isSubscribed={isSubscribed}
          setIsSubscribed={setIsSubscribed}
          setIsVisibleForm={setIsVisibleForm}
        />
      ) : null}
    </>
  );
};

const BackInStock = ({
  currentProduct,
  currentVariant,
  sectionData,
  isVisibleForm,
  setIsVisibleForm,
  isComingSoon,
}) => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [email, setEmail] = useState(
    window.Benchmade.customerEmail ? window.Benchmade?.customerEmail : '',
  );

  const isFormInvalid = false;

  const handleInputChange = (event) => {
    const { value } = event.target;

    setEmail(value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const formData = {
      a: sectionData.settings.klaviyo_public_api,
      platform: 'shopify',
      email,
      variant: currentVariant.id.includes('ProductVariant/')
        ? currentVariant.id.split('ProductVariant/')[1]
        : currentVariant.id,
    };

    postData('/apps/service/klaviyo/api/v1/catalog/subscribe', formData).then(
      ({ status }) => {
        if (status) {
          setIsFormSubmitted(true);
        } else {
          /* eslint-disable-next-line no-console */
          console.error('There was an error submitting the Back in Stock form');
        }
        if (isSubscribed) {
          subscribeToNewsletter(email);
        }
      },
    );
  };

  return (
    <>
      {currentProduct?.tags?.includes('bis_enabled') && !isComingSoon ? (
        <OosFormInner
          currentProduct={currentProduct}
          sectionData={sectionData}
          handleSubmit={handleSubmit}
          isVisibleForm={isVisibleForm}
          isFormSubmitted={isFormSubmitted}
          isFormInvalid={isFormInvalid}
          email={email}
          handleInputChange={handleInputChange}
          isSubscribed={isSubscribed}
          setIsSubscribed={setIsSubscribed}
          setIsVisibleForm={setIsVisibleForm}
        />
      ) : null}
    </>
  );
};

const AddToCartSticky = ({
  currentProduct,
  children,
  sectionData,
  handleOpenForm,
  atcRef,
  isComingSoon,
  isBackInStock,
  isPreOrder,
}) => {
  const productButtons = useRef(null);
  const isInViewPort = useIsInViewport(atcRef);
  const [isStickyActive, setIsStickyActive] = useState(false);
  const [offsetTop, setOffsetTop] = useState(null);
  const { isTablet } = useWindowSize();
  const { body } = document;

  const scrollHandler = (buttonOffsetTop) => {
    if (window.pageYOffset > buttonOffsetTop) {
      setIsStickyActive(true);
      body.classList.add('sticky-buttons-scrolled');
    } else {
      setIsStickyActive(false);
      body.classList.remove('sticky-buttons-scrolled');
    }
  };

  if (!isTablet) {
    if (!isInViewPort) {
      setIsStickyActive(true);
      body.classList.add('sticky-buttons-scrolled');
    } else {
      setIsStickyActive(false);
      body.classList.remove('sticky-buttons-scrolled');
    }
  }

  useEffect(() => {
    const buttonOffsetTop = productButtons.current?.offsetTop;
    setOffsetTop(buttonOffsetTop);

    function scrollListener() {
      scrollHandler(buttonOffsetTop, atcRef);
    }

    if (isTablet) {
      window.addEventListener('scroll', scrollListener);
    } else {
      window.removeEventListener('scroll', scrollListener);
    }

    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTablet, atcRef]);

  const handleScrollTo = () => {
    window.scrollTo({
      behavior: 'smooth',
      top: isTablet ? offsetTop - 80 : offsetTop,
    });
    handleOpenForm();
  };

  return (
    <div
      id="sticky-atc-buttons"
      ref={productButtons}
      className={`top-0 -translate-y-full transition-transform ease-in duration-200  left-0 w-full py-0 flex justify-between z-50 bg-white items-center laptop:px-10 overflow-hidden ${
        isStickyActive
          ? ' translate-y-0 fixed h-auto laptop:py-3 laptop:border-b laptop:border-grey-500'
          : ' h-0'
      } `}
    >
      <h5 className="flex-1 hidden heading-5 laptop:block">
        {currentProduct.title}
      </h5>
      <div className="w-full sticky-atc-buttons laptop:w-auto">
        {/* Coming soon */}
        {isComingSoon && !isPreOrder && (
          <div className="col-span-12 mb-3 laptop:mb-2 atc-buttons">
            <AtcButton
              onClick={handleScrollTo}
              label={sectionData.settings.coming_soon_cta_content}
            />
            <BuildButton currentVariant={currentProduct} />
          </div>
        )}
        {/* Back in stock  */}
        {isBackInStock && !isComingSoon && (
          <div className="col-span-12 mb-3 laptop:mb-2 atc-buttons">
            <AtcButton
              onClick={handleScrollTo}
              label={sectionData.settings.bis_cta_content}
            />
            <BuildButton currentVariant={currentProduct} />
          </div>
        )}
        {/* Out of stock / Add to cart */}
        {children}
      </div>
    </div>
  );
};

const VariantSelector = ({
  variant,
  setCurrentVariant,
  setCurrentProduct,
  currentProduct,
  product,
  isApparel,
  isLast,
  customProduct,
  setYotpoReviewsUpdated,
}) => {
  const selected = currentProduct.id === product.id;
  const handleClick = () => {
    // update Yotpo review widgets
    const productId = product.id.split('gid://shopify/Product/')[1];
    document
      .querySelectorAll('[data-yotpo-widget-instance]')
      .forEach((widget) =>
        widget.setAttribute('data-yotpo-product-id', productId),
      );
    if (typeof window.yotpoWidgetsContainer?.initWidgets === 'function') {
      window.yotpoWidgetsContainer?.initWidgets();
    }
    setYotpoReviewsUpdated(productId); // just to trigger the reviews widget to re-render
  };
  return (
    <div
      className={`${
        isApparel
          ? `w-10 min-w-10 h-10 laptop:w-[45px] laptop:min-w-[45px] laptop:h-[45px]`
          : `w-[74px] min-w-[74px] min-h-[92px] bg-grey-200`
      } swatch-item rounded border overflow-hidden cursor-pointer ${
        // eslint-disable-next-line no-nested-ternary
        selected
          ? 'border-primary-1'
          : isApparel
          ? 'border-transparent'
          : 'border-grey-200'
      } ${isLast && customProduct ? ' mr-3.5 laptop:mr-8' : ''} ${
        isLast && !customProduct ? ' mr-3.5 laptop:mr-10' : ''
      }`}
      style={isApparel ? { backgroundColor: product.apparelColor.value } : {}}
      onClick={() => {
        setCurrentVariant(variant);
        setCurrentProduct(product);
        handleClick();
        document.title = product.title;
      }}
      role="button"
      tabIndex="0"
      aria-label={`Swatch item: ${product.title} `}
    >
      {isApparel ? (
        <div
          className="w-full h-full"
          style={{ backgroundColor: product.apparelColor.value }}
        />
      ) : (
        <Image
          {...variant.image}
          srcWidths={{
            sm: 72,
            md: 72,
            lg: 72,
            xl: 72,
            '2xl': 72,
          }}
          loading="eager"
          className="h-full object-contain max-h-[91px]"
        />
      )}
    </div>
  );
};

const CustomizerSelector = ({ customProduct }) => {
  const [loading, setLoading] = useState(false);
  if (!customProduct) {
    return;
  }

  const handleClick = async () => {
    setLoading(true);
    const customTag = customProduct.tags.find((tag) => tag.includes('cstm:'));
    const threekitAssetId = customProduct?.threekitAssetId?.value || null;
    const threekitDefaultId = customProduct?.threekitDefaultId?.value || null;
    await window.initializeCustomizerPlayer({
      assetId: threekitAssetId,
      customTag,
      handle: customProduct?.handle,
      defaultConfigId: threekitDefaultId,
    });
    setLoading(false);
  };

  // eslint-disable-next-line consistent-return
  return (
    <div
      onClick={handleClick}
      className="w-[74px] min-w-[74px] max-h-[93px] laptop:w-unset rounded border-grey-200 border bg-grey-200 cursor-pointer relative text-center h-full"
    >
      <div className="h-full px-1.5 pb-2.5 pt-3.5 flex items-center flex-col justify-center w-full">
        {loading ? (
          <>
            <ThreekitIcons
              type="loading"
              className="w-[33px] h-[33px] m-auto animate-spin"
            />
            <p className="uppercase font-m-9">Loading</p>
          </>
        ) : (
          <>
            <BuildIcon
              viewBox={`0 0 49 36`}
              className={`!h-auto w-[46px] mx-auto mb-1.5`}
            />
            <p className="uppercase font-m-9">Customize</p>
          </>
        )}
      </div>
    </div>
  );
};

const GiftRecipient = ({
  recipientInfo,
  handleRecipientChange,
  giftMessageInfo,
  maxGiftMessageChars,
}) => {
  const dateRef = useRef(null);
  const today = new Date();

  return (
    <>
      <div className="flex flex-col mt-4 font-r-12">
        <label for="recipient_name" className="mb-2.5 font-[400]">
          {window.TMW.Strings.gift.recipientName.label}
        </label>
        <input
          type="text"
          placeholder={window.TMW.Strings.gift.recipientName.placeholder}
          id="recipient_name"
          name="Recipient name"
          className="h-[50px] font-r-12 font-[325] placeholder:text-black border-black"
          value={recipientInfo['Recipient name']}
          onInput={handleRecipientChange}
        />
      </div>
      <div
        id="recipient_email_container"
        className="flex flex-col mt-5 font-r-12"
      >
        <label for="recipient_email" className="mb-2.5 font-[400]">
          {window.TMW.Strings.gift.recipientEmail.label}
        </label>
        <input
          type="email"
          placeholder={window.TMW.Strings.gift.recipientEmail.placeholder}
          id="recipient_email"
          className="h-[50px] font-r-12 font-[325] placeholder:text-black border-black"
          name="Recipient email"
          value={recipientInfo['Recipient email']}
          onInput={handleRecipientChange}
        />
        <p id="recipient_email_error" className="hidden text-error-dark">
          {window.TMW.Strings.gift.recipientEmail.errorText}
        </p>
      </div>
      <div className="flex flex-col mt-5 font-r-12">
        <label for="gift_message" className="mb-2.5 font-[400]">
          {window.TMW.Strings.gift.giftMessage.label}
        </label>
        <textarea
          placeholder={window.TMW.Strings.gift.giftMessage.placeholder}
          id="gift_message"
          name="Message"
          className="px-5 py-4 font-r-12 font-[325] placeholder:text-black border-black"
          rows="7"
          value={recipientInfo.Message}
          onInput={handleRecipientChange}
          maxLength={maxGiftMessageChars}
        />
        {giftMessageInfo}
      </div>
      <div className="flex flex-col mt-5 font-r-12">
        <p className="mb-2.5 font-[400]">
          {window.TMW.Strings.gift.receiveDate.label}
        </p>
        <div>
          <div className="relative w-full">
            <input
              type="date"
              placeholder={window.TMW.Strings.gift.receiveDate.placeholder}
              id="send_date"
              className="w-full h-[50px] font-r-12 font-[325] placeholder:text-black border-black cursor-pointer"
              name="Send on"
              value={recipientInfo['Send on']}
              onInput={handleRecipientChange}
              ref={dateRef}
              min={today.toISOString().split('T')[0]}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
            />
            {!recipientInfo['Send on'] && (
              <span className="absolute p-3 -translate-y-1/2 bg-white left-2 top-1/2">
                {window.TMW.Strings.gift.receiveDate.placeholder}
              </span>
            )}
            <div
              aria-hidden
              className="pointer-events-none flex items-center h-full border px-3.5 absolute right-0 top-1/2 -translate-y-1/2 bg-white z-1"
            >
              <CalendarIcon />
            </div>
          </div>
          <p id="send_on_error" className="hidden text-error-dark">
            {window.TMW.Strings.gift.receiveDate.errorText}
          </p>
        </div>
      </div>
    </>
  );
};

const ProductForm = ({
  currentVariant,
  currentProduct,
  setCurrentProduct,
  allProducts,
  setCurrentVariant,
  // commented out until we build lifesharp
  // membershipData,
  specificationsLength,
  specificationsData,
  benefitsData,
  element,
  sectionData,
  isOpen,
  setIsOpen,
  setIsOpenAkoa,
  setIsOpenExplore,
  isGift,
  isApparel,
  isSimple,
  formSubmited,
  shippingMessages,
  data,
  gwpThreshold,
}) => {
  const options = currentProduct?.variants?.edges?.map((o) => ({
    label: o.node.title,
    value: o.node.priceV2.amount,
    variant: o.node,
    selected: o.node.id === currentVariant.id,
  }));
  const currentPrice = currentVariant?.priceV2?.amount;
  const currentComparePrice = currentVariant?.compareAtPrice?.amount;
  // eslint-disable-next-line no-nested-ternary
  const sku = isGift
    ? 'Gift Card Denominations'
    : isSimple
    ? currentVariant?.sku
    : currentVariant?.sku;
  const description = currentProduct?.description;
  // commented out until we build lifesharp
  // const initialMembership = membershipData?.memberships[0];
  const hasComingSoonTag = currentProduct?.tags?.includes('coming_soon');
  // commented out until we build lifesharp
  // const [currentMembership, setCurrentMembership] = useState(initialMembership);

  const [isVisibleComingSoonForm, setIsVisibleComingSoonForm] = useState(false);
  const [isVisibleBisForm, setIsVisibleBisForm] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [recipientInfo, setRecipientInfo] = useState({});
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(false);
  // eslint-disable-next-line max-len
  const [sizeGuideBtnExists, setSizeGuideBtnExists] = useState(false); // referring to the button coming from the app
  const [sizeGuideBtn, setSizeGuideBtn] = useState(); // referring to the button coming from the app
  const [yotpoReviewsUpdated, setYotpoReviewsUpdated] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');
  const maxGiftMessageChars = 200;

  // check for size chart button coming from the Clean Size Charts app
  useEffect(() => {
    const MAX_RETRIES = 8;
    let retries = 0;

    const findSizeChartBtn = () => {
      const btn = document.querySelector('#ccpops-trigger');

      if (btn) {
        btn.closest('#ccpops-trigger-container').classList.add('!hidden');
        setSizeGuideBtnExists(true);
        setSizeGuideBtn(btn);
        // eslint-disable-next-line no-use-before-define
        clearInterval(interval);
      } else {
        retries += retries;
        if (retries === MAX_RETRIES) {
          // eslint-disable-next-line no-use-before-define
          clearInterval(interval);
        }
      }
    };

    const interval = setInterval(findSizeChartBtn, 250);
  });

  const handleSizeGuideTriggerClick = () => sizeGuideBtn.click();

  const SizeGuideTrigger = () => (
    <button
      className="font-r-12 mb-2.5 underline"
      type="button"
      onClick={handleSizeGuideTriggerClick}
    >
      Size Guide
    </button>
  );

  const isBackInStock =
    currentProduct?.tags.includes('bis_enabled') &&
    currentVariant.quantityAvailable < 1 &&
    !currentVariant.available;

  const isPreOrder = currentProduct?.tags?.includes('pre-order');

  const hasCstmTag = currentProduct?.tags.some(
    (tag) => tag.includes('cstm:') && tag !== 'cstm:personalization',
  );

  const updateHighlightedReview = (product) => {
    const productId = parseInt(
      (currentProduct?.id || '').split('gid://shopify/Product/')[1],
      10,
    );
    const reviewSection = document.querySelector(
      '[data-section="product-featured-reviews"]',
    );
    const hasComingSoonDate = product?.comingSoonDate?.value;
    const yotpoWidget = document.querySelector('.yotpo.yotpo-shoppers-say');

    if (!reviewSection || !yotpoWidget || !productId) return;
    if (isGift || hasComingSoonDate) {
      reviewSection.classList.add('hidden');
      return;
    }

    yotpoWidget.setAttribute('data-product-id', productId);

    // Re-initialize Yotpo
    if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
      yotpoWidget.innerHTML = '&nbsp;';
      window.yotpo.refreshWidgets();

      let retries = 5; // Number of attempts to check for reviews
      const interval = setInterval(() => {
        const reviewElements =
          yotpoWidget.querySelectorAll('.yotpo-regular-box');
        const hasReviews = reviewElements.length > 0;

        if (hasReviews || retries <= 0) {
          clearInterval(interval);
          reviewSection.classList.toggle('hidden', !hasReviews);
        }
        retries -= 1;
      }, 100);
    } else {
      console.error('Yotpo not initialized');
    }
  };

  useEffect(() => {
    viewItemTrigger(currentProduct, currentVariant);
    updateHighlightedReview(currentProduct);
  }, [currentProduct, currentVariant]);

  const customProduct = hasCstmTag
    ? currentProduct
    : allProducts?.data?.products?.nodes?.find((product) =>
        product.tags.find(
          (tag) => tag.includes('cstm:') && tag !== 'cstm:personalization',
        ),
      );
  const isSoldOut =
    currentVariant.quantityAvailable < 1 && !currentVariant.available;
  const hasComingSoonListId = !!currentProduct?.comingSoonListID?.value;
  const forceNotifyCTA = currentProduct?.forceNotifyCTA?.value === 'true';
  const hasComingSoonDate = currentProduct?.comingSoonDate?.value;

  const showNotifyCTA =
    (hasComingSoonDate && isSoldOut) || (forceNotifyCTA && hasComingSoonListId);
  const showComingSoon = showNotifyCTA
    ? true
    : currentProduct?.comingSoonDate?.value &&
      isSoldOut &&
      new Date(currentProduct?.comingSoonDate?.value) > new Date();
  const showBackInStock =
    !showComingSoon && !hasComingSoonDate && isBackInStock;
  const akoaRequired = currentProduct?.tags?.includes('automatic_knife');

  useAutoOpenCustomizer({
    autoOpen: !(showComingSoon || showBackInStock || isPreOrder),
    assetId: currentProduct?.threekitAssetId?.value,
    defaultConfigId: currentProduct?.threekitDefaultId?.value,
  });

  const atcRef = useRef(null);
  const variantsRef = useRef(null);

  useEffect(() => {
    // Ensure the url changes when the "variant" changes
    if (loaded) {
      const url = `/products/${currentProduct.handle}${
        isGift || isSimple
          ? `?variant=${decodeId(currentVariant.id, 'ProductVariant')}`
          : ''
      }`;
      window.history.pushState({}, '', url);
    }
    setLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentProduct,
    currentVariant,
    isGift,
    isSimple,
    hasComingSoonDate,
    hasComingSoonTag,
  ]);

  useEffect(() => {
    if (variantsRef?.current) {
      makeDivHorizontallyScrollable(variantsRef.current);
    }
  }, [variantsRef]);

  const handleChange = (target, selectOptionSetter, setCurrentVariant) => {
    selectOptionSetter(target.value);
    const currentVariant = options.find(
      (v) => v.variant.priceV2.amount === target.value,
    );
    setCurrentVariant(currentVariant.variant);
  };

  const handleApparelChange = (target, setCurrentVariant) => {
    const currentVariant = options.find(
      (v) =>
        v.variant.title.toLowerCase() === target.dataset.size.toLowerCase(),
    );
    setCurrentVariant(currentVariant.variant);
  };

  const handleRecipientChange = (event) => {
    if (event.target.id === 'gift_message') {
      setGiftMessage(event.target.value);
    }
    const { value, name } = event.target;
    setRecipientInfo({ ...recipientInfo, [name]: value });
  };

  const Dropdown = ({ options, setCurrentVariant }) => {
    const [selectedOption, setSelectedOption] = useState(
      options.find((o) => o.selected).value,
    );
    return (
      <label
        for="option_1"
        className="relative flex w-full h-[50px] overflow-hidden before:content=[''] before:absolute before:top-0 before:right-0 before:w-[50px] before:h-[50px] before:pointer-events-none after:content-[''] before:border before:border-t-0 before:border-b-0 before:border-r-0 after:absolute after:top-0 after:right-0 after:transition-all after:pointer-events-none focus:after:rotate-180 after:bg-arrow-d-orange after:bg-center after:bg-no-repeat after:w-[50px] after:h-[50px]"
      >
        <select
          id="option_1"
          value={selectedOption}
          className="font-m-12 appearance-none border shadow-none flex-[1] px-5 text-primary-1 bg-primary-2 cursor-pointer !ring-0 focus:!ring-0 bg-none min-w-[200px]"
          onChange={(e) =>
            handleChange(e.target, setSelectedOption, setCurrentVariant)
          }
        >
          {options.map((o) => (
            <option key={o.variant.id} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  };

  const productDescription = useMemo(
    () => (
      <div className="relative grid grid-cols-12 col-span-12 mb-[30px] pt-4 border-t border-grey-400">
        {description && (
          <>
            {!isGift && (
              <p className="font-m-12 mb-2.5 col-span-12">
                PRODUCT DESCRIPTION:
              </p>
            )}
            <p className="font-r-12 mb-[15px] col-span-12">
              {isDescriptionVisible ? (
                <span>{description}</span>
              ) : (
                <span className="items-center inline">{`${
                  description.length > 150
                    ? `${description?.substring(0, 150)}... `
                    : description
                }`}</span>
              )}
              {description.length > 150 && (
                <button
                  onClick={() => setIsDescriptionVisible(!isDescriptionVisible)}
                  className="pl-1 underline"
                  type="button"
                  aria-label={
                    isDescriptionVisible
                      ? window.TMW.Strings.product.readLess
                      : window.TMW.Strings.product.readMore
                  }
                >
                  {isDescriptionVisible
                    ? window.TMW.Strings.product.readLess
                    : window.TMW.Strings.product.readMore}
                </button>
              )}
            </p>
          </>
        )}

        <span data-narvar-feature="promiseEddCalculator" />

        <SpecificationLink
          specificationsLength={specificationsLength}
          element={element}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      </div>
    ),
    [currentProduct, isDescriptionVisible],
  );

  const giftMessageInfo = useMemo(
    () => (
      <p className="mt-1 text-xs laptop:text-xs">
        Maximum number of characters: {giftMessage.length}/{maxGiftMessageChars}
      </p>
    ),
    [giftMessage],
  );

  const SizeSelectors = ({ options, setCurrentVariant }) =>
    options.map((o) => (
      <p
        key={o.variant.id}
        data-size={o.label.toLowerCase()}
        onClick={(e) => handleApparelChange(e.target, setCurrentVariant)}
        className={`uppercase cursor-pointer rounded flex items-center justify-center w-10 h-10 laptop:w-[45px] laptop:h-[45px] border ${
          o.selected
            ? 'font-m-10 laptop:font-m-12 border-black'
            : 'font-r-10 laptop:font-r-12 border-grey-400'
        }`}
      >
        {o.label}
      </p>
    ));

  const variantSelectors = useMemo(
    () => (
      <div
        className={clsx(
          'col-span-12',
          { 'mb-10': isGift },
          { 'relative grid grid-cols-12 mb-11': !isGift },
        )}
      >
        {!isApparel && (
          <div
            className={`col-span-12 flex font-r-12 justify-between relative ${
              isGift ? '' : 'mb-3.5 laptop:mb-3.5 laptop:items-start'
            }`}
          >
            {isGift && <span className="mb-2.5 font-[400]">{sku}</span>}

            {!isGift && (
              <div className="inline">
                <p className="block laptop:inline-block laptop:mr-2 mb-2.5">
                  <b className="mr-1 font-m-12">SKU:</b>
                  {sku}
                </p>
                <p className="font-r-12">
                  {specificationsData
                    .filter(
                      (spec) =>
                        spec.key === 'blade_steel' || spec.key === 'handle',
                    )
                    .map((spec, index) => (
                      <span
                        key={`knife-spec-${index}`}
                        className="mr-1 capitalize"
                      >
                        <b className="mr-1 uppercase font-m-12">
                          {spec.key.includes('blade') ? 'Blade' : 'Handle'}:
                        </b>
                        {spec.value}.
                      </span>
                    ))}
                </p>
              </div>
            )}
          </div>
        )}
        <div className={clsx({ 'flex col-span-12 gap-3': !isGift })}>
          <CustomizerSelector customProduct={customProduct} />
          <div
            className={`overflow-hidden ${
              !isGift && customProduct ? 'ml-0 -mr-5 laptop:-mr-10' : ''
            }`}
          >
            {isApparel && (
              <p className="mb-2.5 font-r-12">
                <b className="uppercase font-m-12">Color:</b>{' '}
                {currentProduct.apparelColorName.value}
              </p>
            )}
            <div
              ref={variantsRef}
              className={clsx(
                isApparel && 'mb-[15px] laptop:mb-5 -ml-[21px] -mr-[21px]',
                `ml-0 col-span-12 ${
                  !isGift && customProduct // eslint-disable-line no-nested-ternary
                    ? 'grid-scroll scrollbar-hide pr-5'
                    : !isGift
                    ? 'grid-scroll scrollbar-hide gap-3 laptop:flex laptop:items-start'
                    : ''
                }`,
              )}
            >
              {!isGift &&
                !isSimple &&
                allProducts?.data?.products?.nodes.length > 0 && (
                  <>
                    {allProducts?.data?.products?.nodes?.map(
                      (product, index) => {
                        if (
                          product.tags.includes('lockdown') &&
                          !window.customerAccessTags.includes(
                            `lockdown:${product.handle}`,
                          )
                        ) {
                          return '';
                        }
                        return (
                          <VariantSelector
                            key={product.handle}
                            product={product}
                            variant={product.variants.edges[0].node}
                            setCurrentVariant={setCurrentVariant}
                            currentVariant={currentVariant}
                            setCurrentProduct={setCurrentProduct}
                            currentProduct={currentProduct}
                            isApparel={isApparel}
                            isLast={
                              index ===
                              (allProducts?.data?.products?.nodes.length || 0) -
                                1
                            }
                            customProduct={customProduct}
                            setYotpoReviewsUpdated={setYotpoReviewsUpdated}
                          />
                        );
                      },
                    )}
                  </>
                )}
              {isGift && (
                <>
                  <Dropdown
                    options={options}
                    setCurrentVariant={setCurrentVariant}
                  />
                  <GiftRecipient
                    recipientInfo={recipientInfo}
                    handleRecipientChange={handleRecipientChange}
                    giftMessageInfo={giftMessageInfo}
                    maxGiftMessageChars={maxGiftMessageChars}
                  />
                </>
              )}
            </div>
            {isApparel && (
              <>
                {sizeGuideBtnExists ? (
                  <div className="flex justify-between">
                    <p className="font-m-12 mb-2.5 uppercase">Size:</p>
                    <SizeGuideTrigger />
                  </div>
                ) : (
                  <p className="font-m-12 mb-2.5 uppercase">Size:</p>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  <SizeSelectors
                    options={options}
                    setCurrentVariant={setCurrentVariant}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    ),
    [
      isGift,
      isDescriptionVisible,
      sku,
      description,
      currentProduct,
      specificationsLength,
      element,
      isOpen,
      setIsOpen,
      customProduct,
      isSimple,
      allProducts?.data?.products?.nodes,
      options,
      setCurrentVariant,
      currentVariant,
      setCurrentProduct,
    ],
  );
  // commented out until we build lifesharp
  // const membershipsSelectors = useMemo(
  //   () => (
  //     <div className="grid grid-cols-12 col-span-12 mb-5 laptop:mb-10">
  //       <div className="col-span-12 mb-3.5 flex justify-between">
  //         <label className="font-r-12">
  //           <b className="mr-2">Membership:</b>
  //         </label>
  //         <button
  //           className="flex items-center min-w-fit"
  //           onClick={(e) => {
  //             e.preventDefault();
  //             const container = document.querySelector('[data-lifesharp]');
  //             document.body.classList.add('disable-scroll');
  //             container.classList.add('panel-active');
  //           }}
  //           aria-label="Open lifesharp overlay"
  //         >
  //           <span className="mr-2 font-r-12">About Lifesharp</span>
  //           <ExpandIcon />
  //         </button>
  //       </div>
  //       <div className="grid justify-between grid-cols-12 col-span-12 gap-5 laptop:gap-2">
  //         {membershipData.memberships.map((membership) => (
  //           <MembershipItem
  //             key={membership.title}
  //             setCurrentMembership={setCurrentMembership}
  //             membership={membership}
  //             currentMembership={currentMembership}
  //           />
  //         ))}
  //       </div>
  //     </div>
  //   ),
  //   [currentMembership, membershipData.memberships],
  // );

  const price = useMemo(
    () => (
      <Price
        currentPrice={currentPrice}
        currentComparePrice={currentComparePrice}
        classes={`heading-5`}
        containerClass={`${isGift ? '!justify-start' : ''}`}
      />
    ),
    [currentPrice, currentComparePrice, isGift],
  );

  const stock = useMemo(
    () => (
      <Stock currentVariant={currentVariant} currentProduct={currentProduct} />
    ),
    [currentProduct, currentVariant],
  );

  const benefits = useMemo(() => {
    const madeIn = formatMetaobject(currentProduct.madeIn);
    return (
      <Benefits
        benefitsData={benefitsData}
        setIsOpenExplore={setIsOpenExplore}
        madeIn={madeIn}
      />
    );
  }, [benefitsData, currentProduct, setIsOpenExplore]);

  const afterpay = useMemo(
    () => (
      <Afterpay
        currentVariant={currentVariant}
        currentProduct={currentProduct}
      />
    ),
    [currentProduct, currentVariant],
  );

  const shippingmessage = useMemo(
    () => (
      <ShippingMessage
        currentProduct={currentProduct}
        shippingMessages={shippingMessages}
      />
    ),
    [currentProduct, shippingMessages],
  );

  const reviews = useMemo(
    () => (
      <Reviews
        currentProduct={currentProduct}
        yotpoReviewsUpdated={yotpoReviewsUpdated}
      />
    ),
    [currentProduct],
  );

  const title = useMemo(
    () => (
      <Title
        currentProduct={currentProduct}
        isSimple={isSimple}
        isGift={isGift}
      />
    ),
    [currentProduct, isGift, isSimple],
  );

  const [preorderRestrictionWarningOpen, setPreorderRestrictionWarningOpen] =
    useState(false);

  const onRestrictionContinue = () => {
    const cart = window.GlobalCartContext;
    if (cart?.state?.lines?.length) {
      cart
        .remove(
          cart.state.lines
            .filter(
              (line) =>
                line.merchandise.product?.tags?.includes('pre-order') &&
                line.merchandise.product.id !== currentProduct.id,
            )
            .map((line) => line.id),
          true,
        )
        .then(() => {
          setPreorderRestrictionWarningOpen(false);
          window.openCustomizer(true);
        });
    } else window.openCustomizer();
  };

  useEffect(() => {
    document.addEventListener('preorder-restriction-warning-open', () => {
      setPreorderRestrictionWarningOpen(true);
    });
  }, []);

  return (
    <>
      <CartClearWarningModal
        onContinue={onRestrictionContinue}
        onCancel={() => setPreorderRestrictionWarningOpen(false)}
        isOpen={preorderRestrictionWarningOpen}
        setIsOpen={setPreorderRestrictionWarningOpen}
        warningTitle={window.TMW.Strings.product.preOrderWarning}
        warningText={window.TMW.Strings.product.nonPreOrderWarningText}
      />
      <form
        method="post"
        action="/cart/add"
        accept-charset="UTF-8"
        enctype="multipart/form-data"
        novalidate="novalidate"
        data-product-form
        className={`col-span-12 laptop:col-span-5 px-5 laptop:pl-9 laptop:pr-10 grid-cols-12 grid items-start laptop:top-[38px] laptop:sticky z-20 ${
          isGift ? 'pt-5' : 'pt-[38px]'
        }`}
      >
        {title}
        <div
          className={`grid col-span-12 grid-cols-12 items-center ${
            !isGift
              ? 'border-grey-400 border border-t-0 border-r-0 border-l-0 mb-5'
              : 'mb-10'
          } laptop:mb-10`}
        >
          <div className="grid items-center grid-cols-12 col-span-6">
            <div
              className={`col-span-12 laptop:col-span-6 ${
                isGift ? '' : 'laptop:pt-3.5 laptop:pb-5'
              }`}
            >
              {price}
            </div>
            {!isGift ? stock : null}
          </div>
          {showComingSoon && <CountDown currentProduct={currentProduct} />}
          {!isGift && !hasComingSoonDate && reviews}
        </div>
        {variantSelectors}
        {/* Commented out until we implement lifesharp+ */}
        {/* {!isGift && !isSimple && membershipsSelectors} */}
        <div
          id="atc-buttons"
          className="grid grid-cols-12 col-span-12"
          ref={atcRef}
        >
          {((!showComingSoon && !showBackInStock) || isPreOrder) && (
            <>
              {akoaRequired && (
                <AkoaLink
                  setIsOpenAkoa={setIsOpenAkoa}
                  formSubmited={formSubmited}
                />
              )}
              <AddToCartButton
                currentVariant={currentVariant}
                currentProduct={currentProduct}
                showPrice={false}
                sectionData={sectionData}
                isSimple={isSimple}
                isGift={isGift}
                data={data}
                isBackInStock={isBackInStock}
                isPreOrder={isPreOrder}
                gwpThreshold={gwpThreshold}
                recipientInfo={recipientInfo}
              />
            </>
          )}
        </div>
        {!isGift && (
          <>
            {showComingSoon && (
              <ComingSoon
                sectionData={sectionData}
                currentProduct={currentProduct}
                currentVariant={currentVariant}
                isVisibleForm={isVisibleComingSoonForm}
                setIsVisibleForm={() => setIsVisibleComingSoonForm(true)}
                isComingSoon
              />
            )}
            {!showComingSoon && showBackInStock && (
              <BackInStock
                sectionData={sectionData}
                currentProduct={currentProduct}
                currentVariant={currentVariant}
                isVisibleForm={isVisibleBisForm}
                setIsVisibleForm={() => setIsVisibleBisForm(true)}
              />
            )}
          </>
        )}
        {atcRef && (
          <AddToCartSticky
            sectionData={sectionData}
            currentProduct={currentProduct}
            currentVariant={currentVariant}
            atcRef={atcRef}
            handleOpenForm={() => {
              if (hasComingSoonDate) {
                setIsVisibleComingSoonForm(true);
              } else if (
                currentProduct?.tags.includes('bis_enabled') &&
                currentVariant.quantityAvailable < 1
              ) {
                setIsVisibleBisForm(true);
              }
            }}
            isComingSoon={showNotifyCTA || showComingSoon}
            isBackInStock={showBackInStock}
            isPreOrder={isPreOrder}
          >
            <AddToCartButton
              currentVariant={currentVariant}
              currentProduct={currentProduct}
              showPrice={true}
              isComingSoon={showComingSoon}
              isBackInStock={showBackInStock}
              isPreOrder={isPreOrder}
              formSubmited={formSubmited}
              recipientInfo={recipientInfo}
            />
          </AddToCartSticky>
        )}
        {!isGift && (
          <div className="flex flex-col items-center justify-center col-span-12 laptop:flex-row mb-9">
            {window.Benchmade.showAfterpayMessage && afterpay}
            {shippingmessage}
          </div>
        )}
        {productDescription}
        {!isSimple && benefits}
      </form>
    </>
  );
};

export default ProductForm;
